import React, { useRef } from 'react';
import { Card, Col, Button, Form } from 'react-bootstrap';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function MoodBoardItemCard({
  item,
  isEditing,
  editForm,
  onEditFormChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onImageClick,
  groups = [],
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const editImageRef = useRef(null);
  const isPdf = item.fileType === 'application/pdf';
  const displayImageUrl = item.fileUrl || item.ogImageUrl;
  const hasImage = displayImageUrl && !isPdf;

  // Preview: uploaded file > existing image > image URL typed in form
  const editPreviewUrl = editForm.imageFile
    ? URL.createObjectURL(editForm.imageFile)
    : (item.fileUrl || editForm.imageUrl || null);

  if (isEditing) {
    return (
      <Col xs={12} md={6} lg={4} className="mb-3" ref={setNodeRef} style={style}>
        <Card className="mood-board-card h-100">
          <Card.Body className="p-3">
            <Form.Group className="mb-2">
              <Form.Control
                size="sm"
                value={editForm.name}
                onChange={(e) => onEditFormChange({ ...editForm, name: e.target.value })}
                placeholder="Item name"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Control
                size="sm"
                value={editForm.sourceUrl}
                onChange={(e) => onEditFormChange({ ...editForm, sourceUrl: e.target.value })}
                placeholder="Product URL"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="mb-1" style={{ fontSize: '0.75rem' }}>Image</Form.Label>
              <div className="d-flex gap-2 align-items-center">
                <Form.Control
                  size="sm"
                  value={editForm.imageUrl}
                  onChange={(e) => onEditFormChange({ ...editForm, imageUrl: e.target.value, imageFile: null })}
                  placeholder="Image URL"
                  className="flex-grow-1"
                />
                <input
                  type="file"
                  accept="image/*"
                  ref={editImageRef}
                  className="d-none"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      onEditFormChange({ ...editForm, imageFile: e.target.files[0], imageUrl: '' });
                    }
                  }}
                />
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => editImageRef.current?.click()}
                  title="Upload image"
                  className="flex-shrink-0"
                >
                  <i className="bi bi-upload"></i>
                </Button>
              </div>
              {editForm.imageFile && (
                <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                  <i className="bi bi-check-circle me-1"></i>{editForm.imageFile.name}
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 ms-2 text-danger"
                    onClick={() => onEditFormChange({ ...editForm, imageFile: null })}
                    style={{ fontSize: '0.7rem' }}
                  >
                    Remove
                  </Button>
                </div>
              )}
              {editPreviewUrl && !editForm.imageFile && (
                <div className="mt-1">
                  <img
                    src={editPreviewUrl}
                    alt="Preview"
                    style={{ height: 48, borderRadius: 4, objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </Form.Group>
            {groups.length > 0 && (
              <Form.Group className="mb-2">
                <Form.Select
                  size="sm"
                  value={editForm.groupId || ''}
                  onChange={(e) => onEditFormChange({ ...editForm, groupId: e.target.value || null })}
                >
                  <option value="">Ungrouped</option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group className="mb-2">
              <Form.Control
                size="sm"
                as="textarea"
                rows={2}
                value={editForm.notes}
                onChange={(e) => onEditFormChange({ ...editForm, notes: e.target.value })}
                placeholder="Notes"
              />
            </Form.Group>
            <div className="d-flex gap-2">
              <Button size="sm" variant="primary" onClick={onSaveEdit}>Save</Button>
              <Button size="sm" variant="outline-secondary" onClick={onCancelEdit}>Cancel</Button>
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  }

  return (
    <Col xs={12} md={6} lg={4} className="mb-3" ref={setNodeRef} style={style}>
      <Card className={`mood-board-card h-100 ${isDragging ? 'mood-board-card-dragging' : ''}`}>
        {/* Drag handle */}
        <div className="mood-board-drag-handle" {...attributes} {...listeners}>
          <i className="bi bi-grip-horizontal"></i>
        </div>
        {hasImage && (
          <div className="mood-board-image-wrapper" onClick={() => onImageClick({ url: displayImageUrl, name: item.name })}>
            <Card.Img variant="top" src={displayImageUrl} alt={item.name} className="mood-board-image" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
            {item.sourceUrl && (
              <a
                href={item.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mood-board-source-overlay"
                onClick={(e) => e.stopPropagation()}
                title="Open product page"
              >
                <i className="bi bi-box-arrow-up-right"></i>
              </a>
            )}
          </div>
        )}
        {isPdf && (
          <div className="mood-board-pdf-card d-flex align-items-center justify-content-center"
            onClick={() => window.open(item.fileUrl, '_blank', 'noopener,noreferrer')}
          >
            <div className="text-center">
              <i className="bi bi-file-earmark-pdf-fill text-danger" style={{ fontSize: '2.5rem' }}></i>
              <div className="small text-muted mt-1">{item.fileName || 'Document'}</div>
            </div>
          </div>
        )}
        {!hasImage && !isPdf && (
          <div
            className="mood-board-placeholder d-flex align-items-center justify-content-center"
            onClick={() => item.sourceUrl && window.open(item.sourceUrl, '_blank', 'noopener,noreferrer')}
            style={{ cursor: item.sourceUrl ? 'pointer' : 'default' }}
          >
            <div className="text-center">
              <i className={`bi ${item.sourceUrl ? 'bi-box-arrow-up-right' : 'bi-box-seam'} text-muted`} style={{ fontSize: '2rem' }}></i>
              {item.sourceUrl && (
                <div className="small text-primary mt-1">View product</div>
              )}
            </div>
          </div>
        )}
        <Card.Body className="p-2">
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1 overflow-hidden">
              <div className="fw-medium small text-truncate">{item.name}</div>
              {item.notes && (
                <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>{item.notes}</div>
              )}
              {item.sourceUrl && (
                <a
                  href={item.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-block text-truncate mt-1"
                  style={{ fontSize: '0.75rem' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <i className="bi bi-box-arrow-up-right me-1"></i>
                  {(() => {
                    try { return new URL(item.sourceUrl).hostname; } catch { return item.sourceUrl; }
                  })()}
                </a>
              )}
            </div>
            <div className="d-flex gap-1 flex-shrink-0 ms-2">
              <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => onStartEdit(item)} title="Edit">
                <i className="bi bi-pencil" style={{ fontSize: '0.8rem' }}></i>
              </Button>
              <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => onDelete(item.id)} title="Delete">
                <i className="bi bi-trash" style={{ fontSize: '0.8rem' }}></i>
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
}

export default MoodBoardItemCard;
